import type { StatusTone } from '@vnetwork/ui/data';
import type {
  InvoiceStatus,
  InvoiceType,
  InvoiceSaleType,
  InvoiceStatisticalRow,
} from '@/api/invoices';

/**
 * Invoice domain formatting — status/type/sale-type tones + labels + the
 * statistical aggregator. The `/invoices/statistical` endpoint returns rows
 * grouped by (status × revenue_confirmed × type); the UI needs per-status
 * cards, so we fold them here.
 */

/** Invoice status → @ui tone. */
export function invoiceStatusTone(status?: string): StatusTone {
  switch (status) {
    case 'paid':
    case 'done':
      return 'success';
    case 'approved':
    case 'waiting-confirm':
      return 'info';
    case 'cancelled':
      return 'neutral';
    case 'rejected':
      return 'danger';
    case 'refunded':
      return 'warning';
    default:
      return 'neutral';
  }
}

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  'waiting-confirm': 'Waiting Confirm',
  approved: 'Approved',
  paid: 'Paid',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  done: 'Done',
  refunded: 'Refunded',
};

export const INVOICE_STATUS_OPTIONS: { value: InvoiceStatus; label: string }[] = [
  { value: 'waiting-confirm', label: 'Waiting Confirm' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
];

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  deposit: 'Deposit',
  'refund-deposit-of-customer': 'Refund Deposit',
  draft: 'Draft',
  default: 'Standard',
};

export const INVOICE_TYPE_OPTIONS: { value: InvoiceType; label: string }[] = [
  { value: 'default', label: 'Standard' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'refund-deposit-of-customer', label: 'Refund Deposit' },
  { value: 'draft', label: 'Draft' },
];

export const INVOICE_SALE_TYPE_LABELS: Record<InvoiceSaleType, string> = {
  'new-sell': 'New Sell',
  upsell: 'Upsell',
  renewable: 'Renewable',
};

export function invoiceStatusLabel(status?: string): string {
  if (!status) return '—';
  return INVOICE_STATUS_LABELS[status as InvoiceStatus] ?? status;
}

export function invoiceTypeLabel(type?: string): string {
  if (!type) return '—';
  return INVOICE_TYPE_LABELS[type as InvoiceType] ?? type;
}

/**
 * Normalize `invoice_sale_type` to a plain string code. The list row returns a
 * plain string; the detail endpoint wraps it as `{ invoice_sale_type, valid }`.
 */
export function resolveSaleType(value: unknown): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && 'invoice_sale_type' in value) {
    const inner = (value as { invoice_sale_type?: unknown }).invoice_sale_type;
    return typeof inner === 'string' ? inner : undefined;
  }
  return undefined;
}

export function invoiceSaleTypeLabel(saleType?: unknown): string {
  const code = resolveSaleType(saleType);
  if (!code) return '—';
  return INVOICE_SALE_TYPE_LABELS[code as InvoiceSaleType] ?? code;
}

/** One aggregated stat-band card: summed amount + count for a status. */
export interface InvoiceStatCard {
  status: InvoiceStatus;
  label: string;
  totalAmount: number;
  count: number;
}

/** Order of the stat-band cards. */
const STAT_CARD_ORDER: InvoiceStatus[] = [
  'waiting-confirm',
  'approved',
  'paid',
  'done',
  'cancelled',
  'rejected',
];

/**
 * Fold the grouped statistical rows into per-status cards (summing amount +
 * count across revenue_confirmed / type). Returns cards in display order,
 * omitting statuses with no data.
 */
export function aggregateInvoiceStats(rows: InvoiceStatisticalRow[]): InvoiceStatCard[] {
  const byStatus = new Map<InvoiceStatus, InvoiceStatCard>();
  for (const row of rows) {
    const existing = byStatus.get(row.invoice_status) ?? {
      status: row.invoice_status,
      label: invoiceStatusLabel(row.invoice_status),
      totalAmount: 0,
      count: 0,
    };
    existing.totalAmount += row.total_amount || 0;
    existing.count += row.count || 0;
    byStatus.set(row.invoice_status, existing);
  }
  return STAT_CARD_ORDER.map((s) => byStatus.get(s)).filter((c): c is InvoiceStatCard => !!c);
}
